import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { statsApi } from '@/lib/api'
import { AppIcon, IconLabel } from '@/components/ui/app-icon'
import { ArrowRight, CheckCircle, ClipboardList, Package, Truck } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui'

export function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: async () => (await statsApi.get()).data,
    refetchInterval: 30_000,
  })

  if (isLoading) return <LoadingSpinner />

  const cards = [
    { icon: <Package className="h-6 w-6 text-blue-600" />,       label: 'Номенклатура',        value: stats?.nomenclature_count ?? 0,  bg: 'bg-blue-50' },
    { icon: <ClipboardList className="h-6 w-6 text-orange-600" />, label: 'Активных заданий',    value: stats?.tasks_active ?? 0,        bg: 'bg-orange-50' },
    { icon: <CheckCircle className="h-6 w-6 text-green-600" />,  label: 'Завершённых заданий',  value: stats?.tasks_completed ?? 0,     bg: 'bg-green-50' },
    { icon: <Truck className="h-6 w-6 text-purple-600" />,       label: 'Отгрузок',             value: stats?.shipments_total ?? 0,     bg: 'bg-purple-50' },
  ]

  const referenceCards = [
    {
      icon: <AppIcon name="nomenclature" className="h-7 w-7 text-blue-700" />,
      title: 'Номенклатура',
      description: 'GTIN, бренды и товарные данные для работы с заданиями и печатью.',
      to: '/admin/nomenclature',
      meta: `${(stats?.nomenclature_count ?? 0).toLocaleString('ru-RU')} позиций`,
      accent: 'from-blue-50 to-cyan-50 border-blue-200',
    },
    {
      icon: <AppIcon name="contractor" className="h-7 w-7 text-emerald-700" />,
      title: 'Контрагенты',
      description: 'Список получателей и партнёров для отгрузок и синхронизации с ЭЗ.',
      to: '/admin/contractors',
      meta: 'Ручное ведение и импорт из ЭЗ',
      accent: 'from-emerald-50 to-teal-50 border-emerald-200',
    },
    {
      icon: <AppIcon name="labels" className="h-7 w-7 text-amber-700" />,
      title: 'Шаблоны КА',
      description: 'Управление структурами кодов агрегации для коробок и паллет.',
      to: '/admin/ka-templates',
      meta: 'AI, FNC1 и шаблоны SSCC',
      accent: 'from-amber-50 to-orange-50 border-amber-200',
    },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          <IconLabel icon="dashboard">Панель управления</IconLabel>
        </h1>
        <p className="text-sm text-gray-500">
          Ключевые метрики, быстрые переходы к справочникам и инфраструктурным настройкам.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className={`${c.bg} rounded-lg p-3`}>{c.icon}</div>
              <div>
                <p className="text-sm text-gray-500">{c.label}</p>
                <p className="text-2xl font-bold text-gray-900">{c.value.toLocaleString('ru-RU')}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="space-y-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Справочники</h2>
            <p className="text-sm text-gray-500">
              Быстрый доступ к данным, которые раньше занимали место в боковом меню.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {referenceCards.map((card) => (
            <Link
              key={card.title}
              to={card.to}
              className={`group rounded-2xl border bg-gradient-to-br ${card.accent} p-5 no-underline transition-all hover:-translate-y-0.5 hover:shadow-md`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mb-3">{card.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900">{card.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{card.description}</p>
                </div>
                <span className="rounded-full bg-white/80 p-2 text-gray-500 transition-colors group-hover:text-gray-900">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>

              <div className="mt-5 inline-flex rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-gray-700">
                {card.meta}
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Инфраструктура</h2>
          <p className="text-sm text-gray-500">
            Отдельный вход к техническим настройкам хранения и интеграций.
          </p>
        </div>

        <Link
          to="/admin/infrastructure"
          className="group flex flex-col gap-4 rounded-2xl border border-stone-200 bg-gradient-to-r from-stone-50 via-white to-sky-50 p-6 no-underline transition-all hover:-translate-y-0.5 hover:shadow-md lg:flex-row lg:items-center lg:justify-between"
        >
          <div className="max-w-2xl">
            <div className="mb-3">
              <AppIcon name="infrastructure" className="h-7 w-7 text-stone-700" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Инфраструктура системы</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Настройки MongoDB, Redis и других инфраструктурных зависимостей. Раздел вынесен отдельно от справочников, потому что это не пользовательские данные, а техническая конфигурация среды.
            </p>
          </div>

          <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors group-hover:text-gray-900">
            Открыть раздел
            <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      </section>
    </div>
  )
}
